import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";

import {
	type ColorScheme,
	type ColorTheme,
	parseColorScheme,
	parseColorTheme,
	persistColorScheme,
	persistColorTheme,
	readStoredColorScheme,
	readStoredColorTheme,
} from "@/app/theme";

type ColorThemeContextValue = {
	theme: ColorTheme;
	setTheme: (next: ColorTheme) => void;
	scheme: ColorScheme;
	setScheme: (next: ColorScheme) => void;
};

const ColorThemeContext = createContext<ColorThemeContextValue | null>(null);

export function ColorThemeProvider({ children }: { children: ReactNode }) {
	const [theme, setThemeState] = useState<ColorTheme>(readStoredColorTheme);
	const [scheme, setSchemeState] = useState<ColorScheme>(readStoredColorScheme);

	useEffect(() => {
		document.documentElement.dataset.theme = theme;
		document.documentElement.dataset.scheme = scheme;
	}, [theme, scheme]);

	const value = useMemo<ColorThemeContextValue>(
		() => ({
			theme,
			setTheme: (next: ColorTheme) => {
				const parsed = parseColorTheme(next);
				setThemeState(parsed);
				persistColorTheme(parsed);
			},
			scheme,
			setScheme: (next: ColorScheme) => {
				const parsed = parseColorScheme(next);
				setSchemeState(parsed);
				persistColorScheme(parsed);
			},
		}),
		[theme, scheme],
	);

	return (
		<ColorThemeContext.Provider value={value}>
			{children}
		</ColorThemeContext.Provider>
	);
}

export function useColorTheme(): ColorThemeContextValue {
	const value = useContext(ColorThemeContext);
	if (!value) {
		throw new Error("useColorTheme must be used inside ColorThemeProvider");
	}
	return value;
}
